import { Box, Card, IconButton, Tooltip, Typography } from '@mui/material'
import { ReactElement, memo } from 'react'
import { StreamStep } from 'src/v3/core/types'
import {
  DragAndDropIcon,
  EditIcon,
  PreviewIcon,
  RemoveIcon,
} from 'src/v3/core/icons'
import {
  getStepType,
  getStepIcon,
  getStepLabel,
  getStepIconColor,
} from '../utils/step-type-helpers'

type StepCardProps = {
  step: StreamStep
  contentName: string
  isDragging: boolean
  hideDragHandle?: boolean
  onRemove?: () => void
  onEdit?: () => void
  onPreview?: () => void
}

let StepCard = memo(
  ({
    step,
    contentName,
    isDragging,
    hideDragHandle,
    onRemove,
    onEdit,
    onPreview,
  }: StepCardProps): ReactElement => {
    let stepType = getStepType(step)
    let stepLabel = getStepLabel(stepType)

    return (
      <Card
        variant="outlined"
        className={`flex items-center gap-3 p-3 ${hideDragHandle ? '' : 'cursor-grab active:cursor-grabbing'}`}
        sx={{
          bgcolor: 'background.paperDark',
          opacity: isDragging ? 0.5 : 1,
        }}
        aria-label={`${stepLabel}: ${contentName}`}
      >
        {/* Drag handle */}
        <Box
          className="flex items-center"
          sx={{
            color: 'text.disabled',
            visibility: hideDragHandle ? 'hidden' : 'visible',
          }}
          aria-label={hideDragHandle ? undefined : 'Drag to reorder'}
          role={hideDragHandle ? undefined : 'button'}
          aria-hidden={hideDragHandle ? 'true' : undefined}
        >
          <DragAndDropIcon aria-hidden="true" />
        </Box>

        {/* Type icon */}
        <Box
          className="flex items-center"
          sx={{
            color: getStepIconColor(stepType),
          }}
          aria-hidden="true"
        >
          {getStepIcon(stepType)}
        </Box>

        {/* Content name */}
        <Box className="flex-1 min-w-0">
          <Typography variant="body2" className="truncate">
            {contentName}
          </Typography>
        </Box>

        {/* Video action buttons */}
        {step.videoID && (
          <>
            {onPreview && (
              <Tooltip title="Preview video">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPreview()
                  }}
                  aria-label={`Preview ${contentName}`}
                >
                  <PreviewIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit video">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit()
                  }}
                  aria-label={`Edit ${contentName}`}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

        {/* Show notes action buttons */}
        {step.showNotesID && (
          <>
            {onPreview && (
              <Tooltip title="Preview show notes">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPreview()
                  }}
                  aria-label={`Preview ${contentName}`}
                >
                  <PreviewIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit show notes">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit()
                  }}
                  aria-label={`Edit ${contentName} show notes`}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

        {/* Quiz action buttons */}
        {step.quizID && (
          <>
            {onPreview && (
              <Tooltip title="Preview quiz">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPreview()
                  }}
                  aria-label={`Preview ${contentName}`}
                >
                  <PreviewIcon />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title="Edit quiz">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit()
                  }}
                  aria-label={`Edit ${contentName} quiz`}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

        {/* Remove button */}
        {onRemove && (
          <Tooltip title="Remove from course">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              aria-label={`Remove ${contentName} from course`}
            >
              <RemoveIcon />
            </IconButton>
          </Tooltip>
        )}
      </Card>
    )
  },
)

export default StepCard
